// What a page shows before its route has anything to draw.
//
// Two copies of ONE frame. `index.html` carries it as static markup inside
// `#root`, so the first paint already shows the frame (the stylesheet loads in
// <head> and blocks render; the CSP allows no inline script and no `style=`,
// and the frame needs neither). React empties `#root` at its first commit, and
// that commit is empty: the router starts with no matches and only begins its
// first load in a layout effect after it. `BootGate` puts this same frame back
// in that commit, and keeps it until the router has a match to render, so a
// load never shows a blank page — including a redirect chain (`/` → `/focus`)
// and the guard's wait on `/auth/me`, which on a cold server is seconds. It
// shows again while the error screen's Retry loads the page in place.
//
// The router has no pending component, on purpose. With one, the router hands
// a still-loading match over after `pendingMs`, the pending component mounts a
// SECOND frame where `BootGate`'s was (the bar's sweep restarts), and every
// in-app navigation whose guard waits swaps the page for this frame. Without
// one, the router commits the first load's matches only when they are done, so
// `BootGate`'s frame is one DOM node from React's first commit to the page, and
// an in-app navigation keeps the page it leaves until the next one is ready.
//
// The frame is the `/focus` picker's own (`focus/ui.tsx` `FocusFrame`, the
// project step in `focus/FocusPage.tsx`): brand, a hidden stand-in for the top
// bar's appearance control so the bar has the picker's height, and the hero
// logo. Its only words are the public name (`PUBLIC_PRODUCT_NAME`, which
// `index.html` gets at build time, `brandHtml.ts`) and "Beta", the same in
// every locale, so it needs no translation and the static copy can carry them.
//
// The static copy never moves: plain HTML knows nothing about what is
// happening. This one may (`busy`): it exists only while the router waits on
// the page's first load, and its sweep starts after 600 ms, so a warm load
// never shows motion. `test/boot-shell.test.tsx` pins the two copies to one
// DOM; `test/boot-gate.test.tsx` pins the one node.

import { PUBLIC_PRODUCT_NAME } from "@loom/shared/brand";
import { useRouterState } from "@tanstack/react-router";
import type { JSX, ReactNode } from "react";

import "../theme/theme-toggle.css";
import { LOGO_SRC } from "../ui/logo";

/** The brand word: the public name. `common.brand` is the same in every
 *  locale; the static copy in `index.html` cannot translate, so this one does
 *  not either. */
const BRAND = PUBLIC_PRODUCT_NAME;

export function BootScreen({ busy = true }: { readonly busy?: boolean }): JSX.Element {
  return (
    <div className="loom-focus" aria-busy="true">
      <div className={busy ? "focus-progress is-busy" : "focus-progress"} aria-hidden="true"></div>
      <div className="focus-chrome has-aside">
        <div className="focus-chrome-left">
          <span className="focus-brand">
            <span className="focus-brand-dot"></span>
            {` ${BRAND}`}
          </span>
        </div>
        <div className="focus-chrome-aside focus-boot-hold" aria-hidden="true">
          <span className="theme-toggle">
            <span className="theme-toggle-btn"></span>
            <span className="theme-toggle-btn"></span>
            <span className="theme-toggle-btn"></span>
          </span>
        </div>
        <div className="focus-chrome-right"></div>
      </div>
      <div className="focus-stage">
        <div className="focus-hero">
          <img
            className="focus-hero-logo"
            src={LOGO_SRC}
            width={76}
            height={76}
            alt=""
            aria-hidden="true"
          />
          <span className="focus-hero-beta">Beta</span>
        </div>
      </div>
    </div>
  );
}

/**
 * The router's `InnerWrap`: the boot frame while the router has nothing to
 * draw. That is no match yet (the first load), or a match it shows that is
 * loading again in place (the error screen's Retry: `router.invalidate()` sets
 * a failed match back to `pending`, and with no pending component anywhere the
 * whole tree suspends to an empty fallback). The matches store and React's
 * render both go through `useSyncExternalStore`, so the frame goes and the page
 * arrives in one commit. An error or a not-found is a match too, and draws its
 * own screen.
 */
export function BootGate({ children }: { readonly children: ReactNode }): JSX.Element {
  const booting = useRouterState({
    select: (s) => s.matches.length === 0 || s.matches.some((m) => m.status === "pending"),
  });
  return (
    <>
      {booting ? <BootScreen /> : null}
      {children}
    </>
  );
}
